import React, { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { listAnnouncementsAdmin, saveAnnouncementAdmin, deleteAnnouncementAdmin, type Announcement } from '../../lib/announcements';
import { listCoursesAdmin, type CourseRow } from '../../lib/admin';
import { EmptyState, Panel, PanelHead, Stamp, fieldCls, labelCls } from '../../components/lms/ui';
import { ListRowsSkeleton } from '../../components/lms/Skeletons';

const AnnouncementsAdmin: React.FC = () => {
  const [items, setItems] = useState<Announcement[]>([]);
  const [courses, setCourses] = useState<CourseRow[]>([]);
  const [form, setForm] = useState({ title: '', body: '', courseId: '', isPublished: true });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [notices, programmes] = await Promise.all([
        listAnnouncementsAdmin().catch(() => []),
        listCoursesAdmin().catch(() => []),
      ]);
      setItems(notices);
      setCourses(programmes);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    setError('');
    try {
      await saveAnnouncementAdmin(form);
      setForm({ title: '', body: '', courseId: '', isPublished: true });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the announcement.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] xl:items-start">
      <Panel>
        <PanelHead
          title="Post a notice"
          action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">Student desk</span>}
        />
        <form onSubmit={submit} className="space-y-4 px-5 py-4">
          <p className="max-w-[58ch] text-[12px] leading-5 text-ink-2">
            Notices appear on the student overview and the notice register. Course-scoped posts are only visible to students enrolled in that programme.
          </p>

          <div>
            <label htmlFor="notice-title" className={labelCls}>Title</label>
            <input
              id="notice-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Live session moved to Friday"
              className={fieldCls}
            />
          </div>

          <div>
            <label htmlFor="notice-body" className={labelCls}>Message</label>
            <textarea
              id="notice-body"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={5}
              placeholder="What students need to know, and what to do next…"
              className={fieldCls}
            />
          </div>

          <div>
            <label htmlFor="notice-course" className={labelCls}>Audience</label>
            <select
              id="notice-course"
              value={form.courseId}
              onChange={(e) => setForm({ ...form, courseId: e.target.value })}
              className={fieldCls}
            >
              <option value="">All students (global notice)</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>

          <label htmlFor="notice-publish" className="flex items-center gap-2.5 text-[12.5px] text-ink">
            <input
              id="notice-publish"
              type="checkbox"
              checked={form.isPublished}
              onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
              className="h-4 w-4 rounded-none border-rule accent-pine"
            />
            Publish immediately
          </label>

          {error && (
            <p className="border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger" role="alert">{error}</p>
          )}

          <div className="flex items-center justify-between gap-3 border-t border-rule pt-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">{items.length} on record</p>
            <button type="submit" disabled={busy || !form.title.trim()} className="lms-btn-primary !px-4 !py-2 !text-[12px]">
              {busy ? 'Posting…' : 'Post notice'}
            </button>
          </div>
        </form>
      </Panel>

      <Panel>
        <PanelHead
          title="Notice register"
          action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">{items.length} {items.length === 1 ? 'entry' : 'entries'}</span>}
        />
        {loading ? (
          <ListRowsSkeleton rows={3} label="Loading notices" />
        ) : items.length === 0 ? (
          <EmptyState title="No notices posted" body="Announcements sent from this desk appear here, newest first, with their audience and publication state." />
        ) : (
          <ul className="divide-y divide-rule">
            {items.map((a) => (
              <li key={a.id} className="px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <Stamp tone={a.is_published ? 'cleared' : 'neutral'}>{a.is_published ? 'Published' : 'Draft'}</Stamp>
                      <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">
                        {a.course_id ? courses.find((c) => c.id === a.course_id)?.title || a.course_id : 'All students'} · {new Date(a.created_at).toLocaleString('en-NG')}
                      </span>
                    </div>
                    <p className="mt-2 text-[14px] font-semibold leading-snug text-ink">{a.title}</p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!confirm('Delete this announcement?')) return;
                      await deleteAnnouncementAdmin(a.id);
                      await load();
                    }}
                    className="flex h-7 w-7 flex-none items-center justify-center text-ink-2 transition-colors hover:bg-danger-soft hover:text-danger"
                    aria-label={`Delete ${a.title}`}
                    title="Delete notice"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-[12.5px] leading-5 text-ink-2">{a.body}</p>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
};

export default AnnouncementsAdmin;
